import * as React from "react";
import type { LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";
import { DecoratorNode } from "lexical";

import { cn } from "@/utils";

export type ImagePayload = {
  src: string;
  altText?: string;
  width?: number | "inherit";
  height?: number | "inherit";
};

export type SerializedImageNode = Spread<
  {
    type: "image";
    version: 1;
  },
  {
    src: string;
    altText: string;
    width: number | "inherit";
    height: number | "inherit";
  } & SerializedLexicalNode
>;

const defaultDimensions = {
  width: "inherit" as const,
  height: "inherit" as const
};

function ImageComponent({
  altText,
  src,
  width,
  height
}: {
  altText: string;
  src: string;
  width: number | "inherit";
  height: number | "inherit";
}) {
  const style: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : undefined,
    height: typeof height === "number" ? `${height}px` : undefined
  };

  return (
    <span className="block max-w-full" contentEditable={false}>
      <img
        src={src}
        alt={altText || ""}
        draggable={false}
        className={cn(
          "max-w-full rounded-md border border-border/60 bg-muted object-contain",
          "shadow-sm transition-transform hover:shadow"
        )}
        style={style}
      />
    </span>
  );
}

export class ImageNode extends DecoratorNode<React.ReactNode> {
  __src: string;
  __altText: string;
  __width: number | "inherit";
  __height: number | "inherit";

  static getType(): string {
    return "image";
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      {
        altText: node.__altText,
        width: node.__width,
        height: node.__height
      },
      node.getKey()
    );
  }

  constructor(src: string, payload?: Omit<ImagePayload, "src">, key?: NodeKey) {
    super(key);
    const {
      altText = "",
      width = defaultDimensions.width,
      height = defaultDimensions.height
    } = payload ?? {};

    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { src, altText, width, height } = serializedNode;
    return new ImageNode(src, {
      altText,
      width,
      height
    });
  }

  exportJSON(): SerializedImageNode {
    return {
      type: "image",
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height
    };
  }

  createDOM(): HTMLElement {
    return document.createElement("span");
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.ReactNode {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
      />
    );
  }

  isInline(): false {
    return false;
  }

  extractWithChild(): boolean {
    return false;
  }

  exportDOM(): { element: HTMLElement } {
    const element = document.createElement("img");
    element.setAttribute("src", this.__src);
    if (this.__altText) {
      element.setAttribute("alt", this.__altText);
    }

    return { element };
  }

  getTextContent(): string {
    return this.__altText;
  }

  setSrc(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  setAltText(altText: string): void {
    const writable = this.getWritable();
    writable.__altText = altText;
  }

  setDimensions(width: number | "inherit", height: number | "inherit"): void {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  getSrc(): string {
    return this.__src;
  }

  getAltText(): string {
    return this.__altText;
  }

  getWidth(): number | "inherit" {
    return this.__width;
  }

  getHeight(): number | "inherit" {
    return this.__height;
  }
}

export function $createImageNode({ src, altText, width, height }: ImagePayload): ImageNode {
  return new ImageNode(src, {
    altText,
    width: width ?? defaultDimensions.width,
    height: height ?? defaultDimensions.height
  });
}

export function $isImageNode(node?: LexicalNode | null): node is ImageNode {
  return node instanceof ImageNode;
}
